import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EALogo } from "@/components/brand/EALogo";
import { Button } from "@/components/ui/button";

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [investors, setInvestors] = useState([]);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ investors: 0, prospectus_leads: 0 });
  const [tab, setTab] = useState("investors");
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!loading && (user === false || user?.role !== "admin")) {
      navigate("/admin/login", { replace: true });
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user && user.role === "admin") {
      (async () => {
        setBusy(true);
        try {
          const [inv, lead, st] = await Promise.all([
            api.get("/admin/investors"),
            api.get("/admin/prospectus-leads"),
            api.get("/admin/stats"),
          ]);
          setInvestors(inv.data);
          setLeads(lead.data);
          setStats(st.data);
        } catch (_) {
          // If unauthorized, force logout
          navigate("/admin/login");
        } finally {
          setBusy(false);
        }
      })();
    }
  }, [user, navigate]);

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const exportCSV = (rows, name) => {
    if (!rows.length) return;
    const keys = Object.keys(rows[0]).filter((k) => k !== "id");
    const escape = (v) => {
      if (v == null) return "";
      const s = String(v).replace(/"/g, '""');
      return `"${s}"`;
    };
    const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => escape(r[k])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${name}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (loading || !user || user.role !== "admin") {
    return (
      <div className="min-h-screen bg-ink-950 text-white flex items-center justify-center" data-testid="admin-loading">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">◈ verifying session…</div>
      </div>
    );
  }

  const rows = tab === "investors" ? investors : leads;

  return (
    <div className="min-h-screen bg-ink-950 text-white grid-bg" data-testid="admin-dashboard">
      <header className="border-b border-white/10 sticky top-0 z-30 bg-ink-950/85 backdrop-blur">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="text-spectrum-green"><EALogo size={26} strokeWidth={7} /></span>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/60">
              Svenska Invest / EA · <span className="text-spectrum-green">Admin</span>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden md:inline font-mono text-[10px] uppercase tracking-[0.25em] text-white/50" data-testid="admin-user-email">
              {user.email}
            </span>
            <Button
              onClick={doLogout}
              variant="ghost"
              className="h-9 rounded-none border border-white/15 font-mono text-[10px] uppercase tracking-[0.25em] text-white/80 hover:bg-white hover:text-ink-950 px-4"
              data-testid="admin-logout"
            >
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1600px] px-6 md:px-10 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-spectrum-green mb-3">
            ◢ Console
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter leading-none">
            Lead pipeline.
          </h1>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard label="Investor interest" value={stats.investors} testid="stat-investors" />
          <StatCard label="Prospectus leads" value={stats.prospectus_leads} testid="stat-leads" />
          <StatCard label="Total records" value={stats.investors + stats.prospectus_leads} testid="stat-total" />
          <StatCard label="Sync" value="live" mono testid="stat-sync" />
        </div>

        <div className="flex items-center justify-between border-b border-white/10 mb-6">
          <div className="flex gap-6">
            <TabBtn active={tab === "investors"} onClick={() => setTab("investors")} testid="admin-tab-investors">
              Investors <span className="ml-2 text-white/40">({investors.length})</span>
            </TabBtn>
            <TabBtn active={tab === "leads"} onClick={() => setTab("leads")} testid="admin-tab-leads">
              Prospectus leads <span className="ml-2 text-white/40">({leads.length})</span>
            </TabBtn>
          </div>
          <button
            onClick={() => exportCSV(rows, tab)}
            disabled={!rows.length}
            className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/70 hover:text-spectrum-green disabled:opacity-40 py-3"
            data-testid="admin-export-csv"
          >
            ↓ Export CSV
          </button>
        </div>

        <div className="border border-white/10 overflow-x-auto" data-testid={`admin-table-${tab}`}>
          {busy ? (
            <div className="p-8 font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
              ◈ loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-8 font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
              ◈ no records yet
            </div>
          ) : tab === "investors" ? (
            <table className="w-full">
              <thead className="bg-white/[0.02]">
                <tr className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/40">
                  <Th>When</Th><Th>Name</Th><Th>Email</Th><Th>Amount</Th><Th>Country</Th><Th>Accr.</Th><Th>Note</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {investors.map((r) => (
                  <tr key={r.id} className="hover:bg-white/[0.02]">
                    <Td mono>{fmtDate(r.created_at)}</Td>
                    <Td>{r.name}</Td>
                    <Td mono><a href={`mailto:${r.email}`} className="text-spectrum-green hover:underline">{r.email}</a></Td>
                    <Td mono>{r.amount}</Td>
                    <Td>{r.country || "—"}</Td>
                    <Td>{r.accredited ? "✓" : "—"}</Td>
                    <Td className="max-w-sm truncate" title={r.note}>{r.note || "—"}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full">
              <thead className="bg-white/[0.02]">
                <tr className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/40">
                  <Th>When</Th><Th>Name</Th><Th>Email</Th><Th>Company</Th><Th>IP</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {leads.map((r) => (
                  <tr key={r.id} className="hover:bg-white/[0.02]">
                    <Td mono>{fmtDate(r.created_at)}</Td>
                    <Td>{r.name}</Td>
                    <Td mono><a href={`mailto:${r.email}`} className="text-spectrum-green hover:underline">{r.email}</a></Td>
                    <Td>{r.company || "—"}</Td>
                    <Td mono>{r.ip || "—"}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}

const StatCard = ({ label, value, mono, testid }) => (
  <div className="border border-white/10 bg-ink-900 p-5" data-testid={testid}>
    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">{label}</div>
    <div className={`text-3xl md:text-4xl text-white ${mono ? "font-mono" : "font-display font-black tracking-tighter"}`}>
      {value}
    </div>
  </div>
);

const TabBtn = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`py-4 font-mono text-[11px] uppercase tracking-[0.25em] border-b-2 transition-colors ${
      active ? "text-white border-spectrum-green" : "text-white/50 border-transparent hover:text-white"
    }`}
  >
    {children}
  </button>
);

const Th = ({ children }) => <th className="text-left px-4 py-3 font-normal">{children}</th>;
const Td = ({ children, mono, className = "", title }) => (
  <td className={`px-4 py-3 text-sm ${mono ? "font-mono text-[13px]" : ""} ${className}`} title={title}>{children}</td>
);

function fmtDate(v) {
  try {
    return new Date(v).toISOString().replace("T", " ").slice(0, 16);
  } catch {
    return String(v);
  }
}
